import { z } from "zod";
import { Alert, IdentityStatus, Match, Plan, PlanStatus, PublicUser, Role } from "@snipe/core";

/**
 * The response envelopes, and the three operator shapes that @snipe/core does
 * not own.
 *
 * `Alert`, `Match`, `PublicUser` and `IdentityStatus` are imported and never
 * restated -- a shared contract that gets copied is a shared contract that
 * drifts, and drifting is exactly how a UI starts rendering a field the
 * server stopped sending. The ops shapes below (`OpsSummary`, `AlertRun`,
 * `IdentityHealth`) live in @snipe/db, which is a Postgres driver and a set of
 * queries; importing it here would pull a database client into a browser
 * bundle to obtain six numbers, so they are declared -- and, more usefully,
 * *validated* -- here instead.
 */

/** `GET /auth/me` and `POST /auth/login`. */
export const UserResponse = z.object({ user: PublicUser });

/**
 * Login also answers with `session.token`, for the Expo client that has a
 * keychain to put it in. This schema drops it on the floor: zod strips what
 * it was not asked for, so the browser's copy of the token stops existing at
 * the boundary rather than being politely ignored somewhere downstream.
 */
export const LoginResponse = UserResponse;

export const OpsSummary = z.object({
  alertsTotal: z.number().int(),
  alertsActive: z.number().int(),
  alertsOverdue: z.number().int(),
  runsLastHour: z.number().int(),
  failuresLastHour: z.number().int(),
  matchesLastHour: z.number().int(),
  /** The server's clock when it counted, so a stale panel can admit to it. */
  asOf: z.coerce.date(),
});
export type OpsSummary = z.infer<typeof OpsSummary>;

export const SummaryResponse = z.object({ summary: OpsSummary });

/**
 * `outcome` is a plain string and not an enum on purpose. The API compares it
 * against `'ok'` and treats everything else as a failure; mirroring that with
 * a closed enum here would make a new outcome kind land in the dashboard as a
 * parse error -- a blank screen -- instead of as a row saying something new
 * happened.
 */
export const AlertRun = z.object({
  id: z.string(),
  alertId: z.string(),
  ranAt: z.coerce.date(),
  outcome: z.string(),
  listingsSeen: z.number().int(),
  newMatches: z.number().int(),
  errorKind: z.string().nullable(),
});
export type AlertRun = z.infer<typeof AlertRun>;

export const RunsResponse = z.object({ runs: z.array(AlertRun) });

export function runFailed(run: AlertRun): boolean {
  return run.outcome !== "ok";
}

export const IdentityHealth = z.object({
  id: z.string(),
  label: z.string(),
  status: IdentityStatus,
  liveSessions: z.number().int(),
  soonestExpiryAt: z.coerce.date().nullable(),
  consecutiveFailures: z.number().int(),
  lastMintedAt: z.coerce.date().nullable(),
  lastUsedAt: z.coerce.date().nullable(),
  /**
   * When minting was last TRIED, how it failed, and how many times running.
   *
   * These exist because `lastMintedAt` alone cannot tell "tried and failed"
   * from "never tried", and the dashboard showed `last minted: never` for an
   * hour beside eleven failed runs -- which is what both states look like.
   * `lastMintError` is a kind or a field-naming message from the minter,
   * never a library's text: nothing here can carry a credential.
   */
  lastMintAttemptAt: z.coerce.date().nullable(),
  lastMintError: z.string().nullable(),
  mintFailures: z.number().int(),
});
export type IdentityHealth = z.infer<typeof IdentityHealth>;

export const IdentitiesResponse = z.object({ identities: z.array(IdentityHealth) });

export const BurnResponse = z.object({ burned: z.number().int() });

/** What `POST /ops/identities/:id/mint` answers: ids and clocks, as ever. */
export const MintResponse = z.object({
  minted: z.object({
    sessionId: z.string(),
    expiresAt: z.coerce.date(),
    identity: z.string(),
  }),
});
export type MintResponse = z.infer<typeof MintResponse>;

/** What `POST /ops/identities` answers. A label and a state; no credential. */
export const CreateIdentityResponse = z.object({
  identity: z.object({ id: z.string(), label: z.string(), status: IdentityStatus }),
});
export type CreateIdentityResponse = z.infer<typeof CreateIdentityResponse>;

/**
 * The states a live login passes through. Mirrored from the API rather than
 * imported, for the same reason as `IdentityHealth` -- and kept closed here
 * on purpose: this drives a status line an operator reads while typing a
 * password, so an unknown word must be a visible parse failure rather than a
 * screen that silently says nothing.
 */
export const LiveLoginState = z.enum([
  "starting",
  "waiting",
  "logged_in",
  "timed_out",
  "cancelled",
  "failed",
]);
export type LiveLoginState = z.infer<typeof LiveLoginState>;

/**
 * What `POST /ops/identities/:id/login` answers.
 *
 * A TICKET, and deliberately nothing else. The stream is a live view of a
 * Facebook login, and the one thing this response must never carry is
 * anything that could be replayed against Facebook -- no cookie, no storage
 * state, not a field of one.
 */
export const StartLoginResponse = z.object({
  login: z.object({
    ticket: z.string(),
    state: LiveLoginState,
    identity: z.string(),
    expiresAt: z.coerce.date(),
  }),
});
export type StartLoginResponse = z.infer<typeof StartLoginResponse>;

export const CancelLoginResponse = z.object({ stopped: z.boolean() });

/** One message off the live-login socket: a frame, or a state word. */
export const LiveLoginMessage = z.union([
  z.object({ type: z.literal("frame"), data: z.string() }),
  z.object({ type: z.literal("state"), state: LiveLoginState, identity: z.string() }),
]);
export type LiveLoginMessage = z.infer<typeof LiveLoginMessage>;

export const AlertsResponse = z.object({ alerts: z.array(Alert) });
export const AlertResponse = z.object({ alert: Alert });
export const MatchesResponse = z.object({ matches: z.array(Match), hasMore: z.boolean() });

/** 204 answers carry no body; parsing `null` keeps them on the same path. */
export const NoContent = z.null();

/**
 * A customer as the support routes describe one.
 *
 * Declared rather than reused from `PublicUser` because the two are allowed
 * to diverge: `PublicUser` is what an account is told about ITSELF, and this
 * is what support is shown about somebody else. Keeping them as one type is
 * how a field added for the account holder silently appears on a support
 * screen.
 */
export const SupportUser = z.object({
  id: z.string(),
  email: z.string(),
  role: Role,
  plan: Plan,
  planStatus: PlanStatus,
  createdAt: z.coerce.date(),
});
export type SupportUser = z.infer<typeof SupportUser>;

export const UsersResponse = z.object({ users: z.array(SupportUser) });

/** One customer, with the two lists that answer "I'm not getting alerts". */
export const UserDetailResponse = z.object({
  user: SupportUser,
  alerts: z.array(Alert),
  runs: z.array(AlertRun),
});
export type UserDetail = z.infer<typeof UserDetailResponse>;

export const SupportUserResponse = z.object({ user: SupportUser });

/** A count, because the tokens themselves exist only as hashes. */
export const RevokeSessionsResponse = z.object({ revoked: z.number().int() });

export const DeleteUserResponse = z.object({ deleted: z.boolean() });
